import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button, Grid, Input, Modal, Pagination, Select } from 'antd';
import useIconPickerIndex from '../../hooks/useIconPickerIndex';
import { loadLibraryIconPack, loadLibraryIconPage } from '../../utils/libraryIcons';
import { mayPrefetch, whenIdle } from '../../utils/whenIdle';
import { ALL_ICON_CATEGORIES, OTHER_ICON_CATEGORY, iconLabelOf } from '../../config/iconPicker';
import { RecordIconTile } from './RecordBadge';
import CustomColorPicker from './CustomColorPicker';
import ColorPalette from './ColorPalette';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { ICON_LICENCES_GUIDE_PATH } from '../../config/iconLicences';
import ExternalLink from './ExternalLink';

/**
 * One picker for a record's identity: the icon and the color it sits on,
 * chosen together in a modal, with the badge tile itself as the live
 * preview — what a person is choosing IS "how this record will look", and
 * the two halves only make sense side by side (docs/platform-standards.md
 * § record identity). Used by the card, collection and element-set editors
 * through Input.js InputIconSelect; the profile's avatar uses the icon half
 * alone.
 *
 * A modal rather than the old popover: the library is ~2,200 icons and the
 * grid wants room, and on a phone the sheet takes the whole screen so a
 * tile is a real touch target (64px) instead of a 40px popover cell.
 *
 * LOADING (KPR-1188, KPE-037). The index (names, categories, keywords) is its
 * own chunk, fetched when the modal first opens; tiles are empty same-size
 * cells until it arrives. The artwork is fetched a PAGE at a time, as the
 * pack of each category the page touches (utils/libraryIcons.js
 * loadLibraryIconPage), and the next page and the selected icon's category are
 * prefetched when the browser is idle. A tile never asks for its own file.
 */

const TILE_MIN = 64;
const TILE_MIN_PHONE = 60;
const TILE_GAP = 8;
const GRID_PAD = 2;

// Columns x rows that fit a grid box of width x height, tiles square and at
// least `min` wide. Mirrors the CSS (repeat(cols, 1fr), aspect-ratio 1) so the
// page is exactly full; never below 1 x 1.
export function gridPageSize(width, height, min = TILE_MIN, gap = TILE_GAP) {
	const cols = Math.max(1, Math.floor((width + gap) / (min + gap)));
	const tile = (width - gap * (cols - 1)) / cols;
	const rows = Math.max(1, Math.floor((height + gap) / (Math.max(tile, min) + gap)));
	return { cols, rows, size: cols * rows };
}

// The catalog and its search keywords are the picker's INDEX
// (components/iconPickerIndex.js): a chunk of its own, loaded the first time a
// picker opens (hooks/useIconPickerIndex.js), never part of main.js. What stays
// here is light: the constants, the label, the filter and the modal.
export { ALL_ICON_CATEGORIES, iconLabelOf };
const NO_ICON_ALIASES = {};
const NO_ITEMS = [];
// How long a search's results hold still before their artwork is fetched.
export const SEARCH_SETTLE_MS = 150;

/** Category filter first, then every search word must match name, alias or keyword. */
export function filterIconCatalog(catalog, { query = '', category = ALL_ICON_CATEGORIES } = {}) {
	const inCategory = category === ALL_ICON_CATEGORIES ? catalog : catalog.filter(item => item.category === category);
	const q = `${query || ''}`.toLowerCase().trim();
	if (!q) return inCategory;
	const tokens = q.split(/\s+/).filter(Boolean);
	return inCategory.filter(item => {
		const hay = [item.value, item.label, ...(item.aliases || []), ...(item.keywords || [])]
			.map(s => `${s || ''}`.toLowerCase())
			.join(' ');
		return tokens.every(t => hay.includes(t));
	});
}

/**
 * The modal. `onChange(iconKey | null)` fires as soon as a tile is chosen and
 * the modal stays open so the preview shows the choice; Done closes it.
 * With `onColorChange` the preview row also offers the identity hues and a
 * custom color; without it the picker is the icon alone. `recordKind` is the
 * kind of record being dressed ('card' by default): the preview is the tile
 * it will wear — a collection's is the stack.
 */
export default function IconColorPicker({
	open,
	onClose,
	value,
	onChange,
	color,
	onColorChange = null,
	iconCatalog = null,
	iconAliases = NO_ICON_ALIASES,
	pageSize = 48,
	title,
	recordKind = 'card',
}) {
	const screens = Grid.useBreakpoint();
	const phone = screens.md === false;
	const withColor = typeof onColorChange === 'function';

	const [query, setQuery] = useState('');
	const [category, setCategory] = useState(ALL_ICON_CATEGORIES);
	const [page, setPage] = useState(1);
	const [hoverName, setHoverName] = useState('');
	// The custom color panel opens under the swatches; a fresh open starts closed.
	const [customOpen, setCustomOpen] = useState(false);
	const customPanelId = useId();

	// The library catalog comes from the index chunk; a caller's own catalog
	// (a custom `iconCatalog`) needs none of it.
	const custom = Array.isArray(iconCatalog) && iconCatalog.length ? iconCatalog : null;
	const { status: indexStatus, index, retry: retryIndex } = useIconPickerIndex(open && !custom);
	const catalog = useMemo(() => {
		if (custom) return custom;
		if (!index) return NO_ITEMS;
		const base = index.getLibraryCatalog();
		if (!iconAliases || !Object.keys(iconAliases).length) return base;
		return base.map(item => (iconAliases[item.value] ? { ...item, aliases: iconAliases[item.value] } : item));
	}, [custom, index, iconAliases]);
	const waiting = !custom && !index;

	// Category options: the administered order, plus 'Other' only when
	// something actually falls there. A custom iconCatalog without categories
	// hides the filter entirely (and lists its own categories as they appear).
	const categoryOptions = useMemo(() => {
		const present = new Set(catalog.map(item => item.category).filter(Boolean));
		if (!present.size) return [];
		const ordered = index && !custom ? index.categoryOrder.filter(c => present.has(c)) : [...present].filter(c => c !== OTHER_ICON_CATEGORY);
		if (present.has(OTHER_ICON_CATEGORY)) ordered.push(OTHER_ICON_CATEGORY);
		return [{ value: ALL_ICON_CATEGORIES, label: 'All categories' }, ...ordered.map(c => ({ value: c, label: c }))];
	}, [catalog, index, custom]);

	// A duplicate the picker hides (KPR-1195) is offered only while it is the icon
	// the picker was opened on, so a card that wears one sees it chosen. Judged on
	// the opening value, so choosing another tile does not shift the grid.
	const [openedOn, setOpenedOn] = useState(value);
	useEffect(() => { if (open) setOpenedOn(value); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
	const offered = useMemo(
		() => catalog.filter(item => !item.hidden || item.value === value || item.value === openedOn),
		[catalog, value, openedOn],
	);
	const filtered = useMemo(() => filterIconCatalog(offered, { query, category }), [offered, query, category]);

	useEffect(() => { setPage(1); }, [query, category]);
	// A fresh open starts from the whole library, not last time's search.
	useEffect(() => {
		if (open) { setQuery(''); setCategory(ALL_ICON_CATEGORIES); setPage(1); setHoverName(''); setCustomOpen(false); }
	}, [open]);

	// Measured grid: the page is exactly cols x rows. The prop is the
	// fallback until the grid has a size (and under jsdom, which has none).
	const [gridNode, setGridNode] = useState(null);
	const [fit, setFit] = useState(null);
	const gridRef = useCallback(node => setGridNode(node), []);
	useEffect(() => {
		if (!gridNode) return undefined;
		const measure = () => {
			const w = gridNode.clientWidth - GRID_PAD * 2;
			// Desktop: the CSS caps the grid at 46vh. Phone: it flexes to fill.
			const h = (phone ? gridNode.clientHeight : Math.min(gridNode.clientHeight || Infinity, window.innerHeight * 0.46)) - GRID_PAD * 2;
			if (w > 0 && h > 0 && Number.isFinite(h)) setFit(gridPageSize(w, h, phone ? TILE_MIN_PHONE : TILE_MIN));
		};
		measure();
		if (typeof ResizeObserver === 'undefined') return undefined;
		const ro = new ResizeObserver(measure);
		ro.observe(gridNode);
		return () => ro.disconnect();
	}, [gridNode, phone]);
	const effectiveSize = fit ? fit.size : pageSize;

	// Resizing changes the page size; keep the first visible icon on screen.
	const firstIndex = useRef(0);
	useEffect(() => { setPage(Math.floor(firstIndex.current / effectiveSize) + 1); }, [effectiveSize]);
	firstIndex.current = (page - 1) * effectiveSize;

	const pageStart = (page - 1) * effectiveSize;
	const pageItems = useMemo(() => filtered.slice(pageStart, pageStart + effectiveSize), [filtered, pageStart, effectiveSize]);
	const nextItems = useMemo(() => filtered.slice(pageStart + effectiveSize, pageStart + 2 * effectiveSize), [filtered, pageStart, effectiveSize]);
	const selected = useMemo(() => catalog.find(item => item.value === value) || null, [catalog, value]);
	const selectedCategory = selected ? selected.category : null;
	const browsing = !query.trim();

	// Artwork for the page on screen: one pack request per category it touches
	// (one, for a page of a category), none for icons already held.
	// While a search is being typed each keystroke makes a new page; the artwork
	// is asked for once the results hold still for a moment.
	useEffect(() => {
		if (!open || !index || custom) return undefined;
		const ask = () => loadLibraryIconPage(pageItems.map(item => item.value), index.packOf, { browsing });
		if (browsing) {
			ask();
			return undefined;
		}
		const timer = setTimeout(ask, SEARCH_SETTLE_MS);
		return () => clearTimeout(timer);
	}, [open, index, custom, pageItems, browsing]);

	// When the browser is idle: the next page's artwork, so Next draws at once.
	useEffect(() => {
		if (!open || !index || custom || !nextItems.length || !mayPrefetch()) return undefined;
		return whenIdle(() => loadLibraryIconPage(nextItems.map(item => item.value), index.packOf, { browsing }));
	}, [open, index, custom, nextItems, browsing]);

	// ... and the selected icon's category, which is where the person goes next.
	useEffect(() => {
		if (!open || !index || custom || !selectedCategory || !mayPrefetch()) return undefined;
		return whenIdle(() => {
			const pack = index.packOfCategory(selectedCategory);
			if (pack) loadLibraryIconPack({ ...pack, names: index.namesOfCategory(selectedCategory) });
		});
	}, [open, index, custom, selectedCategory]);

	const heading = title || (withColor ? 'Icon and color' : 'Icon');
	const previewRecord = { icon: value || undefined, color: withColor ? color || undefined : undefined, name: '' };

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={heading}
			width={phone ? '100%' : 760}
			centered={!phone}
			destroyOnClose
			className={`icon-picker${phone ? ' icon-picker--phone' : ''}`}
			footer={[
				value ? <Button key='remove' onClick={() => onChange(null)}>Remove icon</Button> : null,
				<Button key='done' type='primary' onClick={onClose}>Done</Button>,
			]}
		>
			<div className='icon-picker__preview'>
				<RecordIconTile record={previewRecord} kind={recordKind} size={phone ? 72 : 96} fallbackIcon={null} className='icon-picker__preview-tile' />
				<div className='icon-picker__preview-meta'>
					<div className='icon-picker__preview-name'>{selected ? selected.label : value && waiting ? iconLabelOf(value) : 'No icon yet'}</div>
					<div className='icon-picker__preview-hint'>
						{withColor ? 'The tile every reference to this record wears: pick a color, then an icon.' : 'Pick an icon from the library below.'}
					</div>
				</div>
				{withColor ? (
					<ColorPalette
						color={color}
						onPick={onColorChange}
						customOpen={customOpen}
						onToggleCustom={() => setCustomOpen(wasOpen => !wasOpen)}
						customPanelId={customPanelId}
					/>
				) : null}
				{withColor && customOpen ? (
					<CustomColorPicker
						id={customPanelId}
						color={color}
						onChange={onColorChange}
						icon={value}
						recordKind={recordKind}
						phone={phone}
					/>
				) : null}
			</div>

			<div className='icon-picker__filters'>
				<Input
					{...NO_TEXT_ASSIST}
					className='icon-picker__search'
					placeholder='Search icons…'
					allowClear
					value={query}
					onChange={e => setQuery(e.target.value)}
					aria-label='Search icons'
				/>
				{categoryOptions.length ? (
					<Select
						className='icon-picker__category'
						aria-label='Icon category'
						value={category}
						onChange={v => setCategory(v)}
						options={categoryOptions}
						popupMatchSelectWidth={false}
					/>
				) : null}
			</div>

			<div
				className='icon-picker__grid'
				ref={gridRef}
				style={fit ? { gridTemplateColumns: `repeat(${fit.cols}, 1fr)` } : undefined}
				aria-busy={waiting && indexStatus !== 'failed' ? 'true' : undefined}
			>
				{waiting && indexStatus !== 'failed'
					? Array.from({ length: effectiveSize }, (_, i) => <span key={i} className='skeleton icon-picker__tile icon-picker__tile--skeleton' aria-hidden='true' />)
					: null}
				{waiting && indexStatus === 'failed' ? (
					<div className='icon-picker__empty' role='alert'>
						The icon library did not load. <Button type='link' onClick={retryIndex}>Retry</Button>
					</div>
				) : null}
				{pageItems.map(item => (
					<button
						key={item.value}
						type='button'
						className={`icon-picker__tile${value === item.value ? ' is-selected' : ''}`}
						onClick={() => onChange(item.value)}
						onMouseEnter={() => setHoverName(item.label)}
						onMouseLeave={() => setHoverName('')}
						onFocus={() => setHoverName(item.label)}
						onBlur={() => setHoverName('')}
						title={item.label}
						aria-label={item.label}
						aria-pressed={value === item.value}
					>
						{item.iconJSX}
					</button>
				))}
				{!waiting && pageItems.length === 0 ? <div className='icon-picker__empty'>No icons match your search.</div> : null}
			</div>

			{/* The hover label on its own reserved row so the grid never shifts on mouseover; the match count otherwise. */}
			<div className='icon-picker__status' title={hoverName || undefined}>
				{hoverName || (waiting ? '' : `${filtered.length} ${filtered.length === 1 ? 'icon' : 'icons'}`)}
			</div>
			{filtered.length > effectiveSize ? (
				<div className='icon-picker__pages'>
					<Pagination
						size='small'
						current={page}
						pageSize={effectiveSize}
						total={filtered.length}
						showSizeChanger={false}
						showTotal={(total, range) => `${range[0]}\u2013${range[1]} of ${total.toLocaleString()}`}
						onChange={p => setPage(p)}
					/>
				</div>
			) : null}
			<div className='icon-picker__credit'>
				<ExternalLink href={ICON_LICENCES_GUIDE_PATH}>Icons by Tabler, Game Icons and others</ExternalLink>
			</div>
		</Modal>
	);
}
